import React from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Swords, Users, Wine, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  {
    key: "battle",
    icon: Swords,
    title_fr: "Battle 1v1",
    title_en: "Battle 1v1",
    desc_fr: "Duel rapide : 6 questions vin/spiritueux, le plus rapide marque double.",
    desc_en: "Quick duel: 6 wine/spirits questions, fastest correct doubles the score.",
    color: "from-[#E5276A] to-[#9B1B4F]",
  },
  {
    key: "blind",
    icon: Wine,
    title_fr: "Dégustation à l'aveugle",
    title_en: "Blind tasting",
    desc_fr: "geoVINUM AI décrit un vin mystère ; vous proposez couleur, cépage, région. Le plus précis l'emporte.",
    desc_en: "geoVINUM AI describes a mystery wine; you guess colour, grape, region. Most accurate wins.",
    color: "from-[#1F3A5F] to-[#324C73]",
  },
  {
    key: "speed",
    icon: Users,
    title_fr: "Speed-meeting B2B",
    title_en: "B2B speed-meeting",
    desc_fr: "3 minutes, 4 prompts d'icebreaker pour réseauter avec un autre pro du vin.",
    desc_en: "3 minutes, 4 icebreaker prompts to network with another wine pro.",
    color: "from-[#4A5568] to-[#2D3748]",
  },
];

export default function Multiplayer() {
  const { lang } = useT();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(null);

  const startMode = async (mode) => {
    if (!auth.getToken()) {
      toast.error(lang === "fr" ? "Connectez-vous pour jouer en multijoueur" : "Sign in to play multiplayer");
      navigate("/login");
      return;
    }
    setBusy(mode);
    try {
      const { data } = await api.post("/multiplayer/matchmake", { mode });
      navigate(`/multiplayer/${mode}/${data.room_id}`, { state: { role: data.role } });
    } catch (e) {
      toast.error(lang === "fr" ? "Matchmaking échoué" : "Matchmaking failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <div className="max-w-2xl">
        <span className="chip-pink">{lang === "fr" ? "Multijoueur" : "Multiplayer"}</span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
          {lang === "fr" ? "Jouez en direct, avec un autre passionné" : "Play live, with another wine lover"}
        </h1>
        <p className="text-[#4A5568] mt-3">
          {lang === "fr"
            ? "Choisissez un mode. geoVINUM AI vous met en relation avec un autre joueur, puis lance la partie en temps réel."
            : "Pick a mode. geoVINUM AI pairs you with another player and runs the match in real time."}
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              data-testid={`mp-mode-${m.key}`}
              disabled={busy !== null}
              onClick={() => startMode(m.key)}
              className="group text-left rounded-2xl border border-[#1F3A5F]/12 bg-white p-6 hover:border-[#E5276A] hover:shadow-lg transition-all disabled:opacity-60"
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center text-white`}>
                {busy === m.key ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
              </div>
              <h3 className="font-display text-xl text-[#1F3A5F] mt-4">{lang === "fr" ? m.title_fr : m.title_en}</h3>
              <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{lang === "fr" ? m.desc_fr : m.desc_en}</p>
              <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#E5276A] mt-4 group-hover:underline">
                {busy === m.key
                  ? lang === "fr" ? "Recherche d'un adversaire…" : "Looking for an opponent…"
                  : lang === "fr" ? "Lancer la recherche" : "Find opponent"}
                {busy !== m.key && <span aria-hidden>→</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
